import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const CARDS = [
  {
    id: 1,
    num: "01",
    title: "BAKUCHIOL",
    rotation: -6,
    className: "left-[0%] top-[24%] md:top-[26%]",
    img: "https://cdn.jiro.build/Natural%20Glow/Images/in%201.png",
  },
  {
    id: 2,
    num: "02",
    title: "ROSEHIP",
    rotation: 3,
    className: "left-[24%] top-[6%] md:top-[8%]",
    img: "https://cdn.jiro.build/Natural%20Glow/Images/in%202.png",
  },
  {
    id: 3,
    num: "03",
    title: "GREEN TEA",
    rotation: -2,
    className: "left-[48%] top-[24%] md:top-[26%]",
    img: "https://cdn.jiro.build/Natural%20Glow/Images/in%203.png",
  },
  {
    id: 4,
    num: "04",
    title: "SQUALANE",
    rotation: 4,
    className: "left-[72%] top-[6%] md:top-[8%]",
    img: "https://cdn.jiro.build/Natural%20Glow/Images/in%204.png",
  },
];

export default function IngredientsNatural({ className }) {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsContainerRef = useRef(null);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    const titleEl = titleRef.current;
    const cardsContainerEl = cardsContainerRef.current;

    if (!sectionEl || !titleEl || !cardsContainerEl) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionEl,
        start: "top top",
        end: "+=300%",
        pin: true,
        scrub: 1.2,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    tl.fromTo(
      titleEl,
      { x: "70vw", opacity: 0 },
      { x: "0vw", opacity: 1, ease: "power2.out", duration: 1.2 },
      0
    );

    tl.fromTo(
      ".ingredient-card",
      { x: "85vw", opacity: 0, scale: 0.85 },
      {
        x: "0vw",
        opacity: 1,
        scale: 1,
        duration: 1.8,
        ease: "power2.out",
        stagger: 0.38,
      },
      0.5
    );

    tl.to(
      ".ingredient-card",
      {
        x: "-85vw",
        opacity: 0.1,
        ease: "power2.inOut",
        duration: 1.6,
        stagger: 0.35,
      },
      3.2
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@400;700;800&display=swap" rel="stylesheet" />

      <section
        ref={sectionRef}
        className={"relative w-full h-screen bg-[#E9DDCF] overflow-hidden flex flex-col justify-center items-center p-6 sm:p-10 md:p-16 select-none " + (className || "")}
      >
        {/* BACKGROUND HEADING: "Ingredients" */}
        <div className="absolute inset-0 flex items-center justify-center z-0 pointer-events-none overflow-hidden px-2">
          <h2
            ref={titleRef}
            className="whitespace-nowrap tracking-tighter text-center"
            style={{
              color: "#FFFFFF",
              fontFamily: '"Big Shoulders Display", sans-serif',
              fontSize: "clamp(90px, 20vw, 406.912px)",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "1",
              letterSpacing: "-8.138px",
              margin: 0,
            }}
          >
            Ingredients
          </h2>
        </div>

        {/* OVERLAPPING CARDS CONTAINER */}
        <div
          ref={cardsContainerRef}
          className="relative w-full max-w-[1240px] h-[520px] sm:h-[580px] md:h-[640px] lg:h-[680px] mx-auto z-10 flex items-center justify-center"
        >
          {CARDS.map((card) => (
            <div
              key={card.id}
              className={"ingredient-card absolute w-[210px] h-[260px] sm:w-[270px] sm:h-[340px] md:w-[320px] md:h-[400px] lg:w-[350px] lg:h-[440px] bg-[#E5D7C5] rounded-[10px] p-[16px] sm:p-[20px] pb-[22px] sm:pb-[26px] flex flex-col gap-[16px] shadow-sm " + card.className}
              style={{
                transform: "rotate(" + card.rotation + "deg)",
              }}
            >
              <div className="w-full flex-1 min-h-0 rounded-[6px] overflow-hidden bg-[#D1C3B2]/30 flex items-center justify-center p-2">
                <img
                  src={card.img}
                  alt={card.title}
                  className="w-full h-full object-cover rounded-[4px]"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="pt-0 pb-0">
                <h3
                  className="tracking-tight uppercase leading-none text-[#222222]"
                  style={{
                    fontFamily: '"Big Shoulders Display", sans-serif',
                    fontSize: "32px",
                    fontWeight: 800,
                    letterSpacing: "-0.5px",
                  }}
                >
                  {card.num} {card.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}